
import { resolveTicketLayout, agruparObjetosPorRenglon } from "./ticketMotorLayout";
import {
    comandoSeccion,
    renderFilasEsquema,
    RESET_ESTADO_ESTILOS,
} from "./ticketEscPos";
import {
    valorCampoDatos,
    valorCampoDetalle,
    transliterarObjeto,
} from "./ticketLayoutCatalogs";
import { DOTS_POR_MM } from "./ticketPerfil";


const filasDeSeccion = (objetos, obtenerValor) =>
    agruparObjetosPorRenglon(objetos).map((grupo) => ({
        items: grupo.objetos.map((objeto) => ({
            objeto,
            texto: objeto.tipo === "campo"
                ? obtenerValor(objeto.campo, objeto)
                : String(objeto.texto ?? "")
                    .replace(/Remisi[oó]n/gi, "Apartado")
                    .replace(/Folio\s+venta/gi, "Folio Apartado"),
        })),
    }));

const formatearFechaAbono = (valor) => {
    if (!valor) {
        return new Date().toLocaleString("es-MX");
    }

    if (valor instanceof Date) {
        return Number.isNaN(valor.getTime())
            ? ""
            : valor.toLocaleString("es-MX");
    }

    const texto = String(valor).trim();

    // Formato mexicano: DD/MM/YYYY HH:mm:ss
    const coincidencia = texto.match(
        /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/
    );

    if (coincidencia) {
        const [, dia, mes, anio, hora = "00", minuto = "00", segundo = "00"] = coincidencia;

        const fecha = new Date(
            Number(anio),
            Number(mes) - 1,
            Number(dia),
            Number(hora),
            Number(minuto),
            Number(segundo)
        );

        // Evitar aceptar fechas imposibles.
        if (
            fecha.getFullYear() !== Number(anio) ||
            fecha.getMonth() !== Number(mes) - 1 ||
            fecha.getDate() !== Number(dia)
        ) {
            return "";
        }

        return fecha.toLocaleString("es-MX");
    }

    // Compatibilidad con fechas ISO del backend.
    const fecha = new Date(texto);

        return Number.isNaN(fecha.getTime())
            ? ""
            : fecha.toLocaleString("es-MX");
};


export const buildTicketAbonoApartadoParts = ({
    abono,
    parametrosCajaForm,
    formatearImporte,
    esReimpresion = false,
    totalArticuloReimpresion = null,
}) => {
    const layout = resolveTicketLayout(parametrosCajaForm);
    const encoder = new TextEncoder();
    const partes = [];

    const agregar = (texto) => partes.push(encoder.encode(texto));
    const dinero = (valor) => formatearImporte(Number(valor) || 0);

    const {
        iAbono,
        iFolioApartado,
        iIDPartida,
        dMontoAbono,
        dteFechaRegistro,
        sTipoPago,
        sSerialTarjetaChequeVale,
        sNombreCliente,
        sDescripcionArticulo,
        sClaveArticulo,
        dMedida,
        dCantidadArticulo,
        dCostoArticulo,
        dMontoDescuentoArticulo,
        dTotalAbonado,
        sMontoEnLetra,
        EncabezadoTicket,
        PieDeTicket,
    } = abono;

    const costoUnit = Number(dCostoArticulo) || 0;
    const cantidad = Number(dCantidadArticulo) || 0;
    const descuento = Number(dMontoDescuentoArticulo) || 0;

    const totalArticulo =
        esReimpresion && totalArticuloReimpresion !== null
            ? Number(totalArticuloReimpresion)
            : costoUnit * cantidad;

    const totalAbonado = Number(dTotalAbonado) || 0;
    const restante = totalArticulo - totalAbonado;

    // const fecha = dteFechaRegistro
    //     ? new Date(dteFechaRegistro).toLocaleString()
    //     : new Date().toLocaleString();
    const fecha = formatearFechaAbono(dteFechaRegistro);

    const cabecero = transliterarObjeto({
        pc: parametrosCajaForm?.caja || "",
        remision: iFolioApartado || "",
        usuario: parametrosCajaForm?.usuario || "",
        fechaventa: fecha,
        tipopago: sTipoPago || "ABONO",
        razonsocial: sNombreCliente || "No especificado",
        total: Number(dMontoAbono) || 0,
        totalabonado: totalAbonado,
        restante,
        montoletra: sMontoEnLetra || "",
    });

    const detalle = transliterarObjeto({
        clavearticulo: sClaveArticulo || "",
        descripcion: sDescripcionArticulo || "",
        cantidad,
        medida: dMedida || 0,
        costounitario: costoUnit,
        descuento,
        total: totalArticulo,
        abono: Number(dMontoAbono) || 0,
    });

    const dotsPorMm = layout.calibracion?.dotsPorMm || DOTS_POR_MM;
    const anchoCaracterDots = layout.calibracion?.anchoCaracterDots || 9;

    // Inicializacion compatible con el generador de ventas.
    agregar("\x1B\x40");
    agregar("\x1B\x4B\x60");

    const encabezadoTexto = (
        EncabezadoTicket ||
        parametrosCajaForm?.encabezadoticket ||
        ""
    ).trim();

    const encabezado = comandoSeccion(
        encoder,
        layout.cabecero,
        encabezadoTexto
    );

    if (!encabezado.multilinea) agregar("\x1B\x33\x00");
    partes.push(encabezado.bytes);
    if (encabezado.requiereReset) agregar(RESET_ESTADO_ESTILOS);
    if (!encabezado.multilinea) agregar("\x1B\x32");

    // Titulo del comprobante.
    agregar("\x1B\x61\x01\x1B\x45\x01RECIBO DE ABONO\x1B\x45\x00\n");

    if (esReimpresion) {
        agregar("\x1B\x61\x01REIMPRESION DE ABONO\n");
    }

    // Datos personalizados del esquema.
    if (layout.datos.objetos.length > 0) {
        partes.push(...renderFilasEsquema({
            textEncoder: encoder,
            filas: filasDeSeccion(
                layout.datos.objetos,
                (campo) => valorCampoDatos(campo, cabecero)
            ),
            dotsPorMm,
            anchoCaracterDots,
        }));
    }

    // Identificadores propios del abono.
    agregar("\x1B\x61\x00");

    agregar(`Folio Apartado: ${iFolioApartado || ""}\n`);
    agregar(`Partida       : ${iIDPartida || ""}\n`);
    agregar(`Folio Abono   : ${iAbono || ""}\n`);

    if (!layout.datos.objetos.length) {
        agregar(`Fecha         : ${fecha}\n`);
    }

    if (sTipoPago && !layout.datos.objetos.length) {
        agregar(`Tipo Pago : ${sTipoPago}\n`);
    }

    if (sSerialTarjetaChequeVale) {
        agregar(`Referencia : ${sSerialTarjetaChequeVale}\n`);
    }


    // Datos del cliente cuando no existe esquema de datos.
    if (!layout.datos.objetos.length) {
        agregar("\x1B\x61\x01DATOS DEL CLIENTE\n");
        agregar("\x1B\x61\x00");
        agregar(`Cliente: ${sNombreCliente || "N/D"}\n`);
    }

    // Detalle del articulo abonado.
    agregar("\x1B\x61\x01ARTICULO ABONADO\n");
    agregar("\x1B\x61\x00");

    if (layout.detalle.objetos.length > 0) {
        partes.push(...renderFilasEsquema({
            textEncoder: encoder,
            filas: filasDeSeccion(
                layout.detalle.objetos,
                (campo) => valorCampoDetalle(campo, detalle)
            ),
            dotsPorMm,
            anchoCaracterDots,
        }));
    } else {
        agregar(`Clave     : ${sClaveArticulo || "N/D"}\n`);
        agregar(`Descrip.  : ${(sDescripcionArticulo || "N/D").substring(0, 25)}\n`);
        agregar(`Medida    : ${dMedida || 0}\n`);
        agregar(`Cantidad  : ${dCantidadArticulo || 0}\n`);
        agregar(`Importe   : ${dinero(costoUnit)}\n`);
        agregar(`Desc      : ${dinero(descuento)}\n`);
    }

    // Importe del movimiento debajo del detalle del articulo.

    agregar("\x1B\x61\x01\x1B\x45\x01\x1B\x21\x30");
    agregar(`ABONO: ${dinero(dMontoAbono)}\n`);
    agregar("\x1B\x21\x00\x1B\x45\x00\x1B\x61\x00");

    // Estado de cuenta del apartado.
    // En reimpresiones se conserva el saldo actual,
    // igual que en el formato clasico.
    
    agregar(`Total articulo: ${dinero(totalArticulo)}\n`);
    agregar(`Total Abonado: ${dinero(totalAbonado)}\n`);
    agregar(`Restante: ${dinero(restante)}\n`);

    if (sMontoEnLetra) {
        agregar(`Son: ${sMontoEnLetra}\n`);
    }

    // Pie personalizado.
    // No se agregan politicas fijas para evitar duplicados.
    const pieTexto = (
        PieDeTicket ||
        parametrosCajaForm?.pieticket ||
        ""
    ).trim();

    if (pieTexto) {
        const pie = comandoSeccion(encoder, layout.pie, pieTexto);
        partes.push(pie.bytes);
        if (pie.requiereReset) agregar(RESET_ESTADO_ESTILOS);
        agregar("\n");
    }

    // Mensaje final segun el tipo de impresion.
    agregar(
        esReimpresion
            ? "\x1B\x61\x01Comprobante de abono\n"
            : "\x1B\x61\x01\n"
    );

    agregar("\n\n\n");
    agregar("\x1D\x56\x00");

    return { partes };
};
